/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import React, { useContext, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { whenSideBySide, assignFormDesignToCSSVars } from '../fields-customise.style';
import { RadioWrapper, RadioLabel, RadioInput } from './radio_buttons.styled';
import { ctrlDefaults, ControlType } from '../../../../shared/form-lib';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { FormContext } from '../../../shared/contexts/form-context';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import { selectCtrlIdLabelMap } from '../../selectors';
import { ControlPropType } from '../../ctrl_proptype';
import { getStateValue } from '../../../form/utils';
import {
  ClearRadioButtonWrapper,
  RadioFieldsWrapper,
  ValidationWarning,
  MainfieldWrapper,
  SubtextWrapper,
  FieldWrapper,
  LabelWrapper,
  InputWrapper,
  Field,
  Input,
} from '../../common-field-styles';

const RadioTemplate = ({ ctrl, inTable, col, tr, formDesign }) => {
  const [otherTextValue, setOtherTextValue] = useState(ctrl[ControlType.OptOther.STRING] ? ctrl.value : '');
  const { inCustomise, inBuilder, inForm, currentPageNo } = useContext(FormContext);
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];
  const [showOtherTextInput, setShowOtherTextInput] = useState(false);
  const [ctrlValidationError, setCtrlValidationError] = useState(
    getStateValue(ctrl._validationError, col, tr, inTable, inBuilder)
  );

  const dispatch = useDispatch();

  useEffect(() => {
    setCtrlValidationError(getStateValue(ctrl._validationError, col, tr, inTable, inBuilder));
  }, [ctrl._validationError, inTable, col, tr]);

  const saveUpdatedCtrl = (property, newValue) => {
    dispatch(
      setUserInputOnCtrl({
        newValue,
        property,
        page: currentPageNo - 1,
        ctrlId: ctrl.internal_id,
        isValid: true,
        ...(inTable && { tr: tr - 1, col: col - 1 }),
      })
    );
  };

  const handleOtherTextRadioChange = (show) => {
    if (inTable) return;
    
    setShowOtherTextInput(show);
    setOtherTextValue('');
    
    if (ctrl[ControlType.OptOther.STRING] !== show) {
      saveUpdatedCtrl(ControlType.OptOther.STRING, show);
    }
  };

  const handleChange = (e, hideOtherTextInput) => {
    if (hideOtherTextInput) handleOtherTextRadioChange(false);
    saveUpdatedCtrl('value', e.target.value);
  };

  const handleClick = (e) => {
    if (inTable && !ctrl.table_props[col - 1].required && e.target.value === ctrl.value?.[tr - 1]?.[col - 1]) {
      saveUpdatedCtrl('value', '');
    } else if (!inTable && !ctrl.props.required && e.target.value === ctrl.value) {
      saveUpdatedCtrl('value', null);
    }
  };

  const clearSelection = () => {
    handleOtherTextRadioChange(false);
    saveUpdatedCtrl('value', '');
  };

  return (
    <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
      <FieldWrapper>
        {ctrlValidationError && (
          <ValidationWarning className="ui bottom pointing basic label red">{ctrlValidationError}</ValidationWarning>
        )}

        <Field className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
          {!inTable && (
            <LabelWrapper
              notInBuilder={!inBuilder}
              required={ctrl.props.required}
              className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
              <label dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }} />
              <SpecialPropsIndicators ctrl={ctrl} />
            </LabelWrapper>
          )}

          <InputWrapper className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
            {!inTable && (
              <RadioFieldsWrapper className={`${ctrl.props.inline && 'inline'}`} id={`fpl-${ctrl.internal_id}`} inForm>
                {ctrl.props.options.map((option, index) => (
                  <RadioWrapper key={option}>
                    <RadioInput
                      type="radio"
                      value={option}
                      onClick={handleClick}
                      id={`fpl-${ctrl.internal_id}-${index}`}
                      onChange={(e) => handleChange(e, true)}
                      name={`fpl-${ctrl.internal_id}-${index}`}
                      checked={!showOtherTextInput && option === ctrl.value}
                      disabled={ctrl.props.readonly || inCustomise || inBuilder}
                      title={ctrl.props.readonly ? 'This field cannot be edited' : ''}
                    />

                    <RadioLabel
                      notInBuilder={!inBuilder}
                      htmlFor={`fpl-${ctrl.internal_id}-${index}`}
                      title={ctrl.props.readonly ? 'This field cannot be edited' : ''}
                      className={formDesign?.design.form_direction === 'rtl' ? 'rtl' : ''}>
                      {option}
                    </RadioLabel>
                  </RadioWrapper>
                ))}

                {ctrl.props.other && ctrl.props.other.enabled && (
                  <RadioWrapper>
                    <RadioInput
                      type="radio"
                      required={ctrl.props.required}
                      value={ctrl.props.other.label}
                      id={`fpl-${ctrl.internal_id}-other`}
                      name={`fpl-${ctrl.internal_id}-other`}
                      onChange={() => handleOtherTextRadioChange(true)}
                      checked={showOtherTextInput || !!ctrl[ControlType.OptOther.STRING]}
                      title={ctrl.props.readonly ? 'This field cannot be edited' : ''}
                      disabled={ctrl.props.readonly || inCustomise || inBuilder}
                    />

                    <RadioLabel
                      notInBuilder={!inBuilder}
                      htmlFor={`fpl-${ctrl.internal_id}-other`}
                      title={ctrl.props.readonly ? 'This field cannot be edited' : ''}
                      className={formDesign?.design.form_direction === 'rtl' ? 'rtl' : ''}>
                      {ctrl.props.other.label}
                    </RadioLabel>
                  </RadioWrapper>
                )}

                {ctrl[ControlType.OptOther.STRING] && (
                  <InputWrapper>
                    <Input
                      type="text"
                      value={otherTextValue}
                      notInBuilder={!inBuilder}
                      required={ctrl.props.required}
                      onBlur={(e) => handleChange(e)}
                      aria-describedby="descriptive text"
                      placeholder={ctrl.props.other.label}
                      name={`fpl-${ctrl.internal_id}-other-input`}
                      onChange={(e) => setOtherTextValue(e.target.value)}
                      disabled={ctrl.props.readonly || inCustomise || inBuilder}
                    />
                  </InputWrapper>
                )}

                {inForm && !ctrl.props.required && (ctrl.value || ctrl[ControlType.OptOther.STRING]) && (
                  <ClearRadioButtonWrapper onClick={clearSelection} type="button">
                    Clear selection
                  </ClearRadioButtonWrapper>
                )}
              </RadioFieldsWrapper>
            )}

            {inTable && (
              <RadioFieldsWrapper className={`${ctrl.table_props[col - 1].inline && 'inline'}`} inForm>
                {ctrl.table_props[col - 1].options.map((option, index) => (
                  <RadioWrapper key={option}>
                    <RadioInput
                      type="radio"
                      value={option}
                      onClick={handleClick}
                      onChange={(e) => handleChange(e, true)}
                      id={`fpl-${ctrl.internal_id}-${index}-${tr}-${col}`}
                      name={`fpl-${ctrl.internal_id}-${index}-${tr}-${col}`}
                      disabled={ctrl.table_props[col - 1].readonly || inCustomise || inBuilder}
                      checked={ctrl.value && !showOtherTextInput && option === ctrl.value[tr - 1][col - 1]}
                      title={ctrl.table_props[col - 1].readonly ? 'This field cannot be edited' : ''}
                    />

                    <RadioLabel
                      notInBuilder={!inBuilder}
                      htmlFor={`fpl-${ctrl.internal_id}-${index}-${tr}-${col}`}
                      className={formDesign?.design.form_direction === 'rtl' ? 'rtl' : ''}
                      title={ctrl.table_props[col - 1].readonly ? 'This field cannot be edited' : ''}>
                      {option}
                    </RadioLabel>
                  </RadioWrapper>
                ))}

                {ctrl.table_props[col - 1].other && ctrl.table_props[col - 1].other.enabled && (
                  <RadioWrapper>
                    <RadioInput
                      type="radio"
                      value={ctrl.props.other.label}
                      required={ctrl.table_props[col - 1].required}
                      id={`fpl-${ctrl.internal_id}-${tr}-${col}-other`}
                      onChange={() => handleOtherTextRadioChange(true)}
                      name={`fpl-${ctrl.internal_id}-${tr}-${col}-other`}
                      checked={showOtherTextInput || !!ctrl[ControlType.OptOther.STRING]}
                      disabled={ctrl.table_props[col - 1].readonly || inCustomise || inBuilder}
                      title={ctrl.table_props[col - 1].readonly ? 'This field cannot be edited' : ''}
                    />

                    <RadioLabel
                      notInBuilder={!inBuilder}
                      htmlFor={`fpl-${ctrl.internal_id}-${tr}-${col}-other`}
                      className={formDesign?.design.form_direction === 'rtl' ? 'rtl' : ''}
                      title={ctrl.table_props[col - 1].readonly ? 'This field cannot be edited' : ''}>
                      {ctrl.table_props[col - 1].other.label}
                    </RadioLabel>
                  </RadioWrapper>
                )}

                {ctrl[ControlType.OptOther.STRING] && (
                  <RadioWrapper>
                    <RadioInput
                      type="text"
                      value={otherTextValue}
                      notInBuilder={!inBuilder}
                      onBlur={(e) => handleChange(e)}
                      required={ctrl.table_props[col - 1].required}
                      onChange={(e) => setOtherTextValue(e.target.value)}
                      placeholder={ctrl.table_props[col - 1].other.label}
                      name={`fpl-${ctrl.internal_id}-${tr}-${col}-other-input`}
                      disabled={ctrl.table_props[col - 1].readonly || inCustomise || inBuilder}
                    />
                  </RadioWrapper>
                )}
                
                {inForm && ctrl.value?.[tr - 1]?.[col - 1] && !ctrl.table_props[col - 1].required && (
                  <ClearRadioButtonWrapper onClick={clearSelection} type="button">
                    Clear selection
                  </ClearRadioButtonWrapper>
                )}
              </RadioFieldsWrapper>
            )}
            
            {!inTable && (
              <SubtextWrapper id="descriptive text" notInBuilder={!inBuilder}>
                {ctrl.props.subtext}
              </SubtextWrapper>
            )}
          </InputWrapper>
        </Field>
      </FieldWrapper>
    </MainfieldWrapper>
  );
};

RadioTemplate.defaultProps = {
  inTable: false,
  col: null,
  tr: null,
  formDesign: null,
};

RadioTemplate.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  inTable: PropTypes.bool,
  col: PropTypes.string,
  tr: PropTypes.string,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
export default RadioTemplate;
